/**
 * Resizable columns for every `table.bd-table` in the app — installed once
 * (main.tsx), no per-table code: a MutationObserver finds tables as pages
 * render and puts a drag handle on the right edge of each header cell.
 *
 * The table keeps its width (100% of its box): widening a column takes the
 * space from the columns to its right (each down to MIN_W), then from those to
 * its left — nothing is pushed out of the card. Widths are stored as % so a
 * resized window keeps the proportions. Double-click a handle → automatic
 * widths again. Long text wraps in the cells (CSS `.rz-table`).
 *
 * React owns the <th> children it renders; the handle is an extra element it
 * never touches, and a re-created <th> just gets a new handle on the next scan.
 */

const MIN_W = 56;

function headerCells(table: HTMLTableElement): HTMLTableCellElement[] {
  const row = table.tHead?.rows[0];
  return row ? (Array.from(row.cells) as HTMLTableCellElement[]) : [];
}

function apply(table: HTMLTableElement, ths: HTMLTableCellElement[], px: number[]) {
  const total = px.reduce((a, b) => a + b, 0) || 1;
  ths.forEach((th, i) => { th.style.width = `${((px[i] / total) * 100).toFixed(3)}%`; });
  table.style.width = '100%';
  table.style.tableLayout = 'fixed';
}

/**
 * New widths after column `i` changes by `delta` px, the total kept:
 * growing takes from the right-hand columns (nearest first), then the left ones;
 * shrinking gives the space to the next column.
 */
export function redistribute(start: number[], i: number, delta: number, min = MIN_W): number[] {
  const w = [...start];
  if (delta < 0) {
    const give = Math.min(-delta, w[i] - min);
    if (give <= 0) return w;
    w[i] -= give;
    const to = i + 1 < w.length ? i + 1 : i - 1;
    if (to >= 0) w[to] += give;
    return w;
  }
  let need = delta;
  const donors = [...Array.from({ length: w.length - i - 1 }, (_, k) => i + 1 + k), ...Array.from({ length: i }, (_, k) => i - 1 - k)];
  for (const d of donors) {
    if (need <= 0) break;
    const take = Math.min(need, Math.max(0, w[d] - min));
    w[d] -= take;
    need -= take;
  }
  w[i] += delta - need;
  return w;
}

function startDrag(e: PointerEvent, th: HTMLTableCellElement, handle: HTMLElement) {
  const table = th.closest('table') as HTMLTableElement | null;
  if (!table) return;
  e.preventDefault();
  e.stopPropagation();
  const ths = headerCells(table);
  const idx = ths.indexOf(th);
  if (idx < 0) return;
  const start = ths.map((c) => c.getBoundingClientRect().width);
  apply(table, ths, start);
  const startX = e.clientX;
  handle.setPointerCapture(e.pointerId);
  handle.classList.add('rz-active');
  document.body.classList.add('rz-dragging');

  const move = (ev: PointerEvent) => apply(table, ths, redistribute(start, idx, ev.clientX - startX));
  const up = () => {
    handle.removeEventListener('pointermove', move);
    handle.removeEventListener('pointerup', up);
    handle.removeEventListener('pointercancel', up);
    handle.classList.remove('rz-active');
    document.body.classList.remove('rz-dragging');
  };
  handle.addEventListener('pointermove', move);
  handle.addEventListener('pointerup', up);
  handle.addEventListener('pointercancel', up);
}

function reset(th: HTMLTableCellElement) {
  const table = th.closest('table') as HTMLTableElement | null;
  if (!table) return;
  headerCells(table).forEach((c) => { c.style.width = ''; });
  table.style.width = '';
  table.style.tableLayout = '';
}

function enhance(table: HTMLTableElement) {
  table.classList.add('rz-table');
  const ths = headerCells(table);
  ths.forEach((th, i) => {
    if (i === ths.length - 1 || th.querySelector(':scope > .rz-handle')) return;
    const handle = document.createElement('span');
    handle.className = 'rz-handle';
    handle.setAttribute('aria-hidden', 'true');
    handle.title = 'Чирж баганын өргөнийг өөрчлөх · давхар дарвал анхны хэмжээ';
    handle.addEventListener('pointerdown', (e) => startDrag(e, th, handle));
    handle.addEventListener('click', (e) => e.stopPropagation());     // not a sort click
    handle.addEventListener('dblclick', (e) => { e.stopPropagation(); reset(th); });
    th.appendChild(handle);
  });
}

let installed = false;

export function installTableResize(root: HTMLElement = document.body): () => void {
  if (installed) return () => {};
  installed = true;
  let queued = false;
  const scan = () => {
    queued = false;
    root.querySelectorAll<HTMLTableElement>('table.bd-table').forEach(enhance);
  };
  const schedule = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(scan);
  };
  const observer = new MutationObserver(schedule);
  observer.observe(root, { childList: true, subtree: true });
  schedule();
  return () => { observer.disconnect(); installed = false; };
}
